import Button from '@alltrails/shared/denali/components/Button';
import { defineMessages, FormattedMessage } from '@alltrails/shared/react-intl';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import { wrapUrlSafe } from 'utils/language_support_util';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useCurrencyFormatter from 'hooks/useCurrencyFormatter';
import Typography from '@alltrails/shared/denali/components/Typography';
import Logo from '@alltrails/shared/denali/components/Logo';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import { lazy, Suspense } from 'react';
import { NoSsr } from '@material-ui/core';
import * as styles from './styles/styles.module.scss';
import headerImgSrc from './images/welcome-header.jpg';
import headerImgSrcWebp from './images/welcome-header.webp';
import headerImgSrcAvif from './images/welcome-header.avif';
import headerImgMobileSrc from './images/welcome-header-mobile.jpg';
import headerImgMobileSrcWebp from './images/welcome-header-mobile.webp';
import headerImgMobileSrcAvif from './images/welcome-header-mobile.avif';

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

const WelcomeHeader = () => {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const WELCOME_HEADER_STRINGS = defineMessages({
    IMAGE_ALT: {
      defaultMessage: 'A hiker is walking along a trail with mountains in the background.'
    },
    TRY_PLUS_BUTTON: {
      defaultMessage: 'Start your free trial'
    }
  });
  const {
    formattedDefaultMessages: { IMAGE_ALT, TRY_PLUS_BUTTON }
  } = useFormatMessage(WELCOME_HEADER_STRINGS);

  const { plan, currencyCode } = __AT_DATA__;
  const formatter = useCurrencyFormatter(currencyCode);

  const annualCost = Number(plan.priceTotal);
  const monthlyCost = Number(plan.priceMonth);

  return (
    <div className={styles.container}>
      <div className={styles.descriptionContainer}>
        <div className={styles.innerContainer}>
          {/* Add mobile & desktop logos to prevent flicker */}
          <div className={styles.mobileLogo}>
            <Logo variant="plus-logomark" size="xs" color="dark" />
          </div>
          <div className={styles.desktopLogo}>
            <Logo variant="plus-logomark" size="sm" color="dark" />
          </div>
          <Typography variant="heading700" component="h1">
            <FormattedMessage defaultMessage="All your local trails in one app" />
          </Typography>
          <Typography variant="paragraph300">
            <FormattedMessage defaultMessage="Find hikes near you with AllTrails, your companion that leads the way outside. Try AllTrails+ for extra tools like offline maps and more." />
          </Typography>
          <div className={styles.buttonContainer}>
            <Button
              text={TRY_PLUS_BUTTON}
              linkInfo={{
                href: user
                  ? wrapUrlSafe('/plus/checkout', languageRegionCode)
                  : wrapUrlSafe('/signup?returnTo=%2Fplus%2Fcheckout', languageRegionCode)
              }}
              testId="welcome-header-try-pro"
              variant="primary"
            />
            <Typography variant="text100">
              <FormattedMessage
                defaultMessage="Then only {annualCost} a year (that’s {monthlyCost} a month)"
                values={{
                  annualCost: formatter.format(annualCost),
                  monthlyCost: formatter.format(monthlyCost)
                }}
              />
            </Typography>
          </div>
          <div className={styles.chevronContainer}>
            <NoSsr>
              <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                <ChevronDownBouncing />
              </Suspense>
            </NoSsr>
          </div>
        </div>
      </div>
      <div className={styles.imageContainer}>
        <picture>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={headerImgMobileSrcAvif} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={headerImgMobileSrcWebp} type="image/webp" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={headerImgMobileSrc} type="image/jpg" />
          <source srcSet={headerImgSrcAvif} type="image/avif" />
          <source srcSet={headerImgSrcWebp} type="image/webp" />
          <img src={headerImgSrc} alt={IMAGE_ALT} className={styles.image} />
        </picture>
      </div>
    </div>
  );
};

export default WelcomeHeader;
